/**
 * Axios interceptor wiring for the Auth0 access token.
 *
 * When the user is signed in via Auth0, the active access token is attached
 * to every outbound axios request as `Authorization: Bearer ...`. If no token
 * getter is set (Auth0 not configured, or user signed in via local JWT),
 * the interceptor is a no-op — existing local-JWT auth continues to work
 * via the existing axios setup.
 */
import { api } from "@/lib/api";

let tokenGetter = null;
let auth0Logout = null;

export function setAuth0TokenGetter(getter) {
  tokenGetter = getter;
}

/**
 * Register the active Auth0 logout function so `AuthContext.logout()` can
 * also terminate the Auth0 session (instead of merely clearing local
 * storage and silently re-authenticating on the next SSO click).
 */
export function setAuth0Logout(fn) {
  auth0Logout = fn;
}

export function getAuth0Logout() {
  return auth0Logout;
}

api.interceptors.request.use(async (config) => {
  if (!tokenGetter) return config;
  try {
    const token = await tokenGetter();
    if (token) {
      config.headers = config.headers || {};
      // Prefer Auth0 bearer if both are present; existing local JWT path
      // is in the Authorization header set explicitly elsewhere.
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (e) {
    // Token acquisition failure is non-fatal — the request will go out
    // without an Auth0 header and the backend will respond per the local
    // auth path.
  }
  return config;
});
